import { Box, Button, LinearProgress, Snackbar, Stack, TextField, Tooltip, useTheme } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { noop } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COMPONENT_GLOBAL_CONFIG } from "../../constants";
import useDashJSPlayer from '../../hooks/useDashJSPlayer';
import { baseAtom, checkRole, selectCanvasLink, selectInterviewWithInsight } from '../../stores/jotai/base';
import { canvasAtom } from '../../stores/jotai/canvas/canvas';
import { hypothesisToolbarAtom, setHypothesisIdToshowCommentPopup } from '../../stores/jotai/hypothesisToolbar';
import { interviewsAtom } from '../../stores/jotai/interviews/interviews';
import { hasPermissionCanvasWrite } from '../../stores/jotai/permissions';
import { useCreateComment } from '../../tanstackQuery/queries/comments';
import { highlightLinkedEntries, removeHighlightFromEntries } from "../../util/canvasEntryStyleManipulation";
import AlertDialog from '../app/alert-dialog';
import { CanvasCommentsDialog } from './comments-dialog';
import { ENTRY_TOOLBAR_POPOVER_DIMENSIONS, emptyEntryDialogTexts } from './constants';
import CanvasEditingContext from './contexts/CanvasEditingContext';
import CanvasEntryRefContext from './contexts/CanvasEntryRefContext';
import EntryToolbarPopover from './EntryToolbarPopover';
import MaterialBadgeBar from './material-badge-bar';
import MaterialCanvasInsightLinkAI from './material-canvas-insight-link-ai';
import MaterialEntryToolbar from './material-entry-toolbar';
import InsightsDialog from './insights-dialog';

// props needs setHeightMap function in it
// TODO: Make real declerations

export default function CanvasEntry(props) {
    if (props === undefined) {
        return <div></div>
    }

    const { linkedEntryIDs, entry, isDragging, isInArchiveMode, hideModalsInCanvasSection, expandedMode,findingLinksData } = props;
    const [base, setBase] = useAtom(baseAtom);
    const { keycloak } = useKeycloak();
    const navigate = useNavigate()
    const [value, setValue] = useState(props?.entry!.entry);
    const {setIsEditCanvas} = props;
    const [isEditing, setIsEditing] = useState(props?.placeholder === true);
    const [showEmptyValueAlert, setShowEmptyValueAlert] = useState(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const { hypothesisIDBeingEdited, setHypothesisIDBeingEdited } = useContext<any>(CanvasEditingContext);
    const { canvasEntryRefCollection, setCanvasEntryRefCollection } = useContext<any>(
        CanvasEntryRefContext
    );
    const interviews = useAtomValue(interviewsAtom);    
    const theme = useTheme();
    const entryRef = useRef(null);

    // canvas state tells us which entry has the insights and comments pane open
    // by extracting this out to the global state we can open it with links
    const canvasState = useAtomValue(canvasAtom);
    const [isEntryToolbarVisible, setIsEntryToolbarVisible] = useState<boolean>(false);
    const { selectedHypothesisIDToFilter } = canvasState;
    const [localLineTargets, setLocalLineTargets] = useState([]);
    const [hypothesisToolbarAtomState, setHypothesisToolbarAtomState] = useAtom(
        hypothesisToolbarAtom
    );
    const [showInsightPopup, setShowInsightPopup] = useState(false);
    const [showLinkPopup, setShowLinkPopup] = useState(false);
    //const [entryComments, setEntryComments] = useState([]);
    const [changePopUpHeight, setChangePopUpHeight] =useState(null); 

    const { dashJSPlayer } = useDashJSPlayer();
    const { hypothesisIdToshowCommentPopup } = hypothesisToolbarAtomState;
    const showCommentPopup: boolean = hypothesisIdToshowCommentPopup === entry?.entryID;

    const client = useQueryClient();
    const { mutateAsync: createNewComment } = useCreateComment(
      { accessToken: keycloak?.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["comments"] });
        }
      }
    );

    const entryComments = () => {
        return props?.comments?.filter(
            (comment) => comment?.rating == props?.entry?.entryID
        )
    }

    const [localError, setLocalError] = useState(null);
    const [isDeletEntryModalOpen, setIsDeletEntryModalOpen] = useState(false);

    const canEditCanvas = hasPermissionCanvasWrite(base) && !isInArchiveMode;
    const entryInputRef = useRef(null);
    const defaultBoxShadow = `0 0 0 1px ${theme?.custom?.borderGrey}`;
    const blueBoxShadow = "0 0 0 3px #1976D2";
    const grayBoxShadow = "0 0 0 3px #757575";

    const entriesToHighlight = linkedEntryIDs
        ?.filter((id) => id !== hypothesisIDBeingEdited)
        .map((id) => canvasEntryRefCollection[id]);

    const entriesToFade = Object.keys(canvasEntryRefCollection).filter(
        (id) => !linkedEntryIDs.includes(id)
    );

    const openEntryToolbar = () => {
        if (!props.placeholder && !isInArchiveMode) {
            setIsEntryToolbarVisible(true);
        }
    }

    const closeEntryToolbar = () => {
        setIsEntryToolbarVisible(false);
    }

    const handleEntryToolbarVisibility = useCallback(() => {
        const { entry } = props;

        if (!isEntryToolbarVisible) {
            openEntryToolbar();
        } else {
            closeEntryToolbar();
        }
    }, [
        props?.entry?.entryID,
        isEntryToolbarVisible,
        props?.placeholder,
        openEntryToolbar,
        closeEntryToolbar,
    ]);

    const closeCommentPopup = () => {
        if (entry.entryID === hypothesisIdToshowCommentPopup) {
            setHypothesisIdToshowCommentPopup(
                setHypothesisToolbarAtomState,
                null
            );

            if (!selectedHypothesisIDToFilter) {
                removeHighlightFromEntries({ canvasEntryRefCollection,theme });
            }
        }
    };

    const openCommentPopover = () => {
        setHypothesisIdToshowCommentPopup(
            setHypothesisToolbarAtomState,
            entry?.entryID
        );
    };

    const closeInsightPopup = () => {
        setShowInsightPopup(false);

        if (!selectedHypothesisIDToFilter) {
            removeHighlightFromEntries({ canvasEntryRefCollection,theme });
        }

        if (dashJSPlayer.instance) {
            dashJSPlayer.instance.pause();
            //    dashJSPlayer.instance.destroy();
        }
    };

    const openInsightPopover = () => {
        setShowInsightPopup(true);
    };

    const closeLinkPopup = () => {
        selectCanvasLink(setBase, undefined);
        setShowLinkPopup(false);

        if (!selectedHypothesisIDToFilter) {
            removeHighlightFromEntries({ canvasEntryRefCollection,theme });
        }
    };

    const openLinkPopover = () => {
        setShowLinkPopup(true);
    };

    const shouldDisableEntryToolbarTriggerEvents =
        props?.placeholder ||
        isDragging ||
        showCommentPopup ||
        showInsightPopup ||
        showLinkPopup;

    useEffect(() => {
        if (
            isEntryToolbarVisible &&
            (props?.placeholder ||
                isDragging ||
                showCommentPopup ||
                showInsightPopup ||
                showLinkPopup)
        ) {
            closeEntryToolbar();
        }
    }, [
        isEntryToolbarVisible,
        props?.placeholder,
        isDragging,
        showCommentPopup,
        showInsightPopup,
        showLinkPopup,
    ]);

    useEffect(() => {
        const { toolbarVisibilityStateSetterRef, entry } = props;

        if (isEntryToolbarVisible) {
            toolbarVisibilityStateSetterRef.current = {
                entryID: entry?.entryID,
                setter: setIsEntryToolbarVisible,
            };
        } else {
            if (toolbarVisibilityStateSetterRef.current?.entryID === entry?.entryID) {
                toolbarVisibilityStateSetterRef.current = {
                    entryID: null,
                    setter: noop,
                };
            }
        }
    }, [isEntryToolbarVisible, props?.toolbarVisibilityStateSetterRef]);

    useEffect(() => {
        if (isInArchiveMode) {
            closeEntryToolbar();
        }
    }, [isInArchiveMode]);

    useEffect(() => {
        if (!isEditing && entryInputRef?.current) {
            entryInputRef.current.blur()
        }

        if (isEditing) {
            setHypothesisIDBeingEdited(entry?.entryID);
        } else {
            setHypothesisIDBeingEdited(null);
        }

        if (entryRef.current) {
            const element = entryRef.current;

            if (isEditing) {
                element.style.boxShadow = blueBoxShadow;
            } else {
                if (!!selectedHypothesisIDToFilter) {
                    element.style.boxShadow = grayBoxShadow;
                } else {
                    // element.style.boxShadow = defaultBoxShadow;
                }
            }
        }
    }, [isEditing])

    useEffect(() => {
        if (entryRef.current) {
            setCanvasEntryRefCollection(prevCollection => ({
                ...prevCollection,
                [entry?.entryID]: entryRef.current,
            }))
        }
    }, [entryRef.current]);

    useEffect(() => {
        if (!selectedHypothesisIDToFilter) {
            entriesToHighlight.forEach((element) => {
                if (element) {
                    element.style.boxShadow = defaultBoxShadow;
                }
            });
        }
    }, [selectedHypothesisIDToFilter]);

    useEffect(() => {
        if (showCommentPopup && !hideModalsInCanvasSection) {
            entryRef.current.scrollIntoView({ block: "center" });
        }
    }, [showCommentPopup])

    useEffect(() => {
        if (typeof entry?.entry === "string") {
            setValue(entry?.entry);
        } else if(typeof entry === "string") {
            setValue(entry);
        } else {
            setValue(undefined);
        }
    }, [entry]);

    const dialogRef1 = useRef(null);

    const getAttachedFindings = useCallback((entry) => {
        let findingsForThisEntry = [];

        for (let i = 0; i < findingLinksData?.findingLinks?.length; i++) {
            if (findingLinksData?.findingLinks[i]?.entryID === entry?.entryID) {
                const findingID = findingLinksData?.findingLinks[i]?.findingID;
                
                let finding = findingLinksData?.finding?.[findingID];
                if (finding !== undefined && !finding?.isDraft) {
                    findingsForThisEntry.push(finding);
                }
            }
        }

        return findingsForThisEntry;
    }, [findingLinksData?.findingLinks, props?.entry]);

    const handleEmptyValue = () => {
        setValue(props?.entry?.entry);
        setShowEmptyValueAlert(true);
    };

    const valueChanged = () => {
        return props?.entry?.entry !== value;
    };

    const onSubmitEditedValue = () => {
        if (!props?.placeholder) {
            if (value === "") {
                handleEmptyValue();
            } else if (valueChanged()) {
                props.onSave(value, setIsSaving, setValue);
            }

            setIsEditing(false);
            setIsEditCanvas(false);
            props.onClose();
        } else {
            if (value !== "" && valueChanged()) {
                setIsSaving(true);
                props.onSave(value, setIsSaving, setValue);
                // setValue("");
            }
        }
    };

    let renderedEntry = undefined;

    const handleMouseEnter = () => {
        if (
            !selectedHypothesisIDToFilter &&
            !props?.placeholder &&
            !base?.canvasLinkTarget &&
            !isDragging
        ) {
            highlightLinkedEntries({
                entriesToHighlight,
                entriesToFade,
                canvasEntryRefCollection,
            });
        }
    };

    const handleMouseLeave = () => {
        if (
            !selectedHypothesisIDToFilter &&
            !props?.placeholder &&
            !base?.canvasLinkTarget &&
            !isDragging
        ) {
            removeHighlightFromEntries({
                canvasEntryRefCollection,
                hypothesisIDBeingEdited,
                theme
            });
        }
    };

    const handleHypothesisBlurEvent = (event) => {
        if (isEditing) {
            event.preventDefault();
            onSubmitEditedValue();
            setIsEditing(false);
            setIsEditCanvas(false);
            props.onClose();
        }
    }

    const getIsDisabled = () => {
        if (canEditCanvas !== true) {
            return true;
        }

        return isInArchiveMode;
    }
    let isDisabled = getIsDisabled();

    const renderInput = useMemo(() => {
        return (
            <TextField
                inputRef={entryInputRef}
                multiline
                autoFocus={props?.placeholder}
                className="iw-component-input-noresize iw-component-input-minimal canvas-entry-material-textfield font-weight-normal"
                sx={{
                    padding: props?.placeholder ? "0 0 0 22.5px" : 0,
                    ".MuiInputBase-root": {
                        ".MuiOutlinedInput-input.Mui-disabled": {
                            "WebkitTextFillColor": "rgba(0, 0, 0, 0.5)",
                        },
                        "textarea": {
                            lineHeight: "26px !important",
                        }
                    }
                }}
                value={value}
                variant="outlined"
                disabled={isDisabled || isSaving}
                onFocus={() => {
                    setIsEditing(true);
                    setIsEditCanvas(true)
                }}
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        e.preventDefault();
                        setIsEditing(false);
                        setIsEditCanvas(false);
                        props.onClose();
                    } else if (e.key === "Enter"  && !e.shiftKey) {
                        e.preventDefault();
                        onSubmitEditedValue();
                    }
                }}
                onBlur={onSubmitEditedValue}
                onChange={(e) => {
                    setValue(e.target.value);
                }}
                placeholder='Type a new hypothesis here.'
            />
        );
    }, [
        value,
        isEditing,
        isSaving,
        props?.placeholder,
        isInArchiveMode,
        isDisabled,
    ]);

    const getBoxShadow = () => {
        if (isEditing) {
            return blueBoxShadow;
        }

        if (props?.highlighted) {
            return `0 0 0 1px ${props?.highlighted}`;
        }

        return "none";
    }

    const openDeleteEntryModal = () => {
        setIsDeletEntryModalOpen(true);
    };

    if (props?.entry !== undefined && props?.entry.state !== undefined) {
        if (props?.entry?.state?.name !== 'Deleted') {
            let style = {
                border:"1px solid #CECECE",
                position: 'relative',
                padding: '7px 12px 7px 0',
                backgroundColor: props?.entryBackgroundColor,
                transition: 'box-shadow 0.1s ease, opacity 0.1s ease',
                boxShadow: getBoxShadow(),
                cursor: canEditCanvas ? "grab" : "auto",
                ':hover': {
                    boxShadow: isEditing
                        ? blueBoxShadow
                        : grayBoxShadow,
                }
            }

            if (isEditing) {
                style.boxShadow = '0 0 0 3px #1976D2';
            }

            if (props?.entry?.state?.name === 'Validated') {
                style.backgroundColor = "#BFE3C0";
            }

            if (props?.entry?.state?.name === 'Invalidated') {
                style.backgroundColor = '#F1ADAC';
            }


            renderedEntry = (
                <Tooltip
                    open={isEntryToolbarVisible}
                    disableFocusListener={shouldDisableEntryToolbarTriggerEvents}
                    disableHoverListener={shouldDisableEntryToolbarTriggerEvents}
                    disableTouchListener={shouldDisableEntryToolbarTriggerEvents}
                    PopperProps={{
                        sx: {
                            zIndex: "1500 !important",
                            margin: "0 -4px -5px 0px !important",
                            padding: "0px !important",
                            "& .MuiTooltip-tooltip": {
                                margin: "0px !important",
                                padding: "0px !important",
                            },
                        },
                    }}
                    classes={{
                        popper: "canvas-popper",
                    }}
                    onOpen={handleEntryToolbarVisibility}
                    onClose={handleEntryToolbarVisibility}
                    enterNextDelay={COMPONENT_GLOBAL_CONFIG.CANVAS_ENTRY.HOVER_OVER_TOOLBAR.ENTER_NEXT_DELAY}
                    enterDelay={COMPONENT_GLOBAL_CONFIG.CANVAS_ENTRY.HOVER_OVER_TOOLBAR.ENTER_DELAY}
                    arrow={false}
                    leaveDelay={50}
                    title={
                        isDragging ? null : (
                            <MaterialEntryToolbar
                                broadcastHeightChange={(key, newHeight) => {
                                    if (key !== null && key !== undefined) {
                                        if (newHeight !== null && newHeight !== undefined) {
                                            props.setHeightMap(newHeight, key);
                                        }
                                    }
                                }}
                                csEntries={props?.csEntries}
                                rootNode={props?.rootNode}
                                barRef={entryRef}
                                hasParentCateggory={
                                    props.parentCategoryID !== undefined
                                }
                                app={props?.app}
                                cohort={props?.cohort}
                                team={props?.team}
                                attachedFindings={getAttachedFindings(props?.entry)}
                                org={props?.org}
                                user={props?.user}
                                comments={entryComments()}
                                entry={props?.entry}
                                findingLinksData={findingLinksData?.finding}
                                placeholder={props?.placeholder}
                                isDisabled={isDisabled}
                                highlighted={props?.highlighted}
                                onChangeState={(state) => {
                                    props.onChangeState(state, setIsSaving, setValue);
                                }}
                                onClose={() => {
                                    props.onClose();
                                }}
                                entryBackgroundColor={props?.entryBackgroundColor}
                                commentContextID={props?.commentContextID}
                                closeEntryToolbar={() => {
                                    closeEntryToolbar();
                                    handleMouseLeave();
                                }}
                                openCommentPopover={openCommentPopover}
                                openInsightPopover={openInsightPopover}
                                openLinkPopover={openLinkPopover}
                                openDeleteEntryModal={openDeleteEntryModal}
                            />
                        )
                    }
                    placement="top-end"
                >
                    <Box
                        ref={entryRef}
                        className="canvas-draghandle-parent"
                        key={
                            "box-canvas-entry-" + JSON.stringify(props?.entry?.entryID)
                        }
                        sx={{...style}}
                    >
                        <div ref={dialogRef1} />

                        <Stack
                            direction="row"
                            spacing={0}
                            sx={{ alignItems: "center" }}
                        >
                            {props.placeholder ? undefined : props.dragHandle()}
                            {renderInput}
                        </Stack>

                        {isSaving && (
                            <LinearProgress
                                sx={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                }}
                            />
                        )}
                    </Box>
                </Tooltip>
            );
        }
    }

    if (renderedEntry === undefined) {
        return <div></div>
    }

    if (
        !!selectedHypothesisIDToFilter &&
        !linkedEntryIDs.includes(selectedHypothesisIDToFilter) &&
        entry.entryID != selectedHypothesisIDToFilter
    ) {
        return <></>;
    }

    const renderLineTargets = () => {
        return <></>
    }

    const updateComment = ({ payload, commentContextID, entryID, onSuccess = noop, onFailure = noop }) => {
        let token = undefined;

        try {
            token = keycloak.token;
        } catch (e) { }

        let sid = undefined;

        try {
            //@ts-ignore
            sid = window.__sid;
        } catch (e) { }

        createNewComment({
          ...payload,
          commentContextID,
          entryID,
          cohortID: props?.cohort?.id,
          sid: sid
        })
          .then((res) => {
            onSuccess(res);
          })
          .catch((error) => {
            onFailure(error);
          }); 

    };

    const handleEntryStateChange = (state) => {
        const isTeamMemberOrAdmin =
            checkRole(base, "isTeamMember") ||
            checkRole(base, "isCohortAdmin");

        if (isTeamMemberOrAdmin) {
            if (props?.entry?.state?.name === state) {
                props.onChangeState("Hypothesis", setIsSaving, setValue);
            } else {
                props.onChangeState(state, setIsSaving, setValue);
            }
        } else {
            setLocalError(
                "Only team members can validate & invalidate hypothesis"
            );
        }
    };

    return (
        <div
            style={{
                position: "relative",
                display: "block",
                width: "100%",
                backgroundColor: "transparent",
                padding: "5px 0",
                height: "100%",
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {renderLineTargets()}
            {renderedEntry}
            {!isEntryToolbarVisible && !props?.placeholder && (
                <MaterialBadgeBar
                    entry={props?.entry}
                    allComments={entryComments()}
                    findingLinksData={findingLinksData?.finding}
                />
            )}
            <AlertDialog
                title={emptyEntryDialogTexts?.title}
                description={emptyEntryDialogTexts?.description}
                open={showEmptyValueAlert}
                confirmButton={
                    <Button onClick={() => setShowEmptyValueAlert(false)}>
                        Understood
                    </Button>
                }
            />
            {!hideModalsInCanvasSection && (
                <>
                    <EntryToolbarPopover
                        open={showCommentPopup}
                        anchorEl={entryRef.current}
                        onClose={closeCommentPopup}
                        width={
                            ENTRY_TOOLBAR_POPOVER_DIMENSIONS.COMMENT_POPOVER
                                .WIDTH
                        }
                        hideBackdrop={expandedMode}
                        focusHypothesis={true}
                    >
                        {/* @ts-ignore */}
                        <CanvasCommentsDialog
                            closeCommentView={closeCommentPopup}
                            updateComment={updateComment}
                            authToken={keycloak?.token}
                            user={base?.user}
                            org={{
                                ...base?.org,
                                members: base?.members,
                            }}
                            cohort={base?.cohort}
                            team={base?.team}
                            queryClient={client}
                            comments={props?.comments?.filter(
                                (comment) =>
                                    comment.rating == props?.entry?.entryID
                            )}
                            entryContent={props?.entry}
                            entrySegment={props?.canvasSegment}
                            targetCommentID={props?.canvasTargetComment}
                            entryID={props?.entry?.entryID}
                            theme={theme}
                        />
                    </EntryToolbarPopover>
                    <EntryToolbarPopover
                        open={showInsightPopup}
                        anchorEl={entryRef.current}
                        onClose={closeInsightPopup}
                        width={
                            ENTRY_TOOLBAR_POPOVER_DIMENSIONS.INSIGHT_POPOVER
                                .WIDTH
                        }
                        hideBackdrop={expandedMode}
                        focusHypothesis={true}
                    >
                        <InsightsDialog 
                            attachedFindings={getAttachedFindings(entry)}
                            findingLinks={entry?.findingLinks}
                            findingLinksUser={findingLinksData?.user}
                            closeCommentView={closeInsightPopup}
                            validate={() => handleEntryStateChange("Validated")}
                            invalidate={() =>
                                handleEntryStateChange("Invalidated")
                            }
                            canEditCanvas={canEditCanvas}
                            updatePopupHeight={(newHeight) =>
                                setChangePopUpHeight(newHeight)
                            }
                        />
                    </EntryToolbarPopover>
                    <EntryToolbarPopover
                        open={showLinkPopup}
                        anchorEl={entryRef.current}
                        onClose={closeLinkPopup}
                        width={
                            ENTRY_TOOLBAR_POPOVER_DIMENSIONS.LINK_POPOVER.WIDTH
                        }
                        hideBackdrop={expandedMode}
                        focusHypothesis={true}
                    >
                        <MaterialCanvasInsightLinkAI
                            csEntries={props.csEntries}
                            entryID={entry?.entryID}
                            close={closeLinkPopup}
                        />
                    </EntryToolbarPopover>
                </>
            )}
            <Snackbar
                open={localError !== null}
                autoHideDuration={6000}
                onClose={() => {
                    setLocalError(null);
                }}
                message={localError}
            />
        </div>
    );
}
